import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  CopyButton,
  FileInput,
  Group,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconClock,
  IconCopy,
  IconExternalLink,
  IconEye,
  IconPlus,
  IconUpload,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import { fetchPageAnalytics, uploadHostedPage } from '../services/api';

function extractTitle(html) {
  const match = /<title[^>]*>([^<]*)<\/title>/i.exec(html || '');
  return match ? match[1].trim() : '';
}

function formatDuration(seconds) {
  if (!seconds) return '0s';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return mins ? `${mins}m ${secs}s` : `${secs}s`;
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

export default function HostedPagesPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);

  const [opened, { open, close }] = useDisclosure(false);
  const [creating, setCreating] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  function resetForm() {
    setFile(null);
    setTitle('');
    setBodyHtml('');
  }

  async function handleFile(selected) {
    setFile(selected);
    if (!selected) {
      setBodyHtml('');
      setTitle('');
      return;
    }
    const ext = selected.name.split('.').pop().toLowerCase();
    if (ext === 'zip') {
      setTitle(selected.name.replace(/\.[^.]+$/, ''));
      setBodyHtml('__zip__'); // sentinel so the publish button enables
      return;
    }
    try {
      const text = await selected.text();
      setBodyHtml(text);
      setTitle(extractTitle(text) || selected.name.replace(/\.[^.]+$/, ''));
    } catch (err) {
      notifications.show({ title: 'Could not read file', message: err.message, color: 'red' });
    }
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPageAnalytics(api);
      setPages(data.pages || []);
      setTotals(data.totals || null);
    } catch (err) {
      notifications.show({
        title: 'Failed to load hosted pages',
        message: err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  async function handleCreate(e) {
    e.preventDefault();
    if (!file) {
      notifications.show({ title: 'No file selected', message: 'Choose an HTML or ZIP file.', color: 'red' });
      return;
    }
    const isZip = file.name.toLowerCase().endsWith('.zip');
    if (!isZip && !bodyHtml.trim()) {
      notifications.show({ title: 'Empty file', message: 'The HTML file appears to be empty.', color: 'red' });
      return;
    }

    setCreating(true);
    try {
      const created = await uploadHostedPage(api, file);
      const assetMsg = created.assetCount > 0
        ? ` ${created.assetCount} asset${created.assetCount === 1 ? '' : 's'} uploaded.`
        : '';
      notifications.show({ title: 'Page published', message: `"${created.title}" is now hosted.${assetMsg}`, color: 'teal' });
      resetForm();
      close();
      await loadData();
      navigate(`/hosting/${created.slug}`);
    } catch (err) {
      notifications.show({
        title: 'Failed to create page',
        message: err.response?.data?.detail || err.response?.data?.error || 'Something went wrong',
        color: 'red',
      });
    } finally {
      setCreating(false);
    }
  }

  const statCards = [
    { label: 'Total Views', icon: IconEye, value: totals?.totalViews ?? 0, color: 'aviary' },
    { label: 'Unique Visitors', icon: IconUsers, value: totals?.uniqueVisitors ?? 0, color: 'teal' },
    { label: 'Avg. Time on Page', icon: IconClock, value: formatDuration(totals?.avgTimeOnPage), color: 'grape' },
    { label: 'Hosted Pages', icon: IconWorld, value: pages.length, color: 'blue' },
  ];

  return (
    <Box p="md" bg="white" h="100%">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={700} size="lg">Hosted Pages</Text>
            <Text c="dimmed" size="sm">All Aviary-hosted content and how it is performing.</Text>
          </div>
          <Button leftSection={<IconPlus size={14} />} onClick={open}>
            Add Page
          </Button>
        </Group>

        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          {statCards.map((card) => (
            <Paper key={card.label} radius="md" p="lg" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" c="dimmed" fw={500}>{card.label}</Text>
                <ThemeIcon variant="light" color={card.color} size="lg" radius="md">
                  <card.icon size={18} />
                </ThemeIcon>
              </Group>
              <Text fw={700} size="xl">{card.value}</Text>
            </Paper>
          ))}
        </SimpleGrid>

        {pages.length === 0 && !loading ? (
          <Text c="dimmed" ta="center" py="xl">
            No hosted pages yet. Pages created via the bot API will appear here.
          </Text>
        ) : (
          <ScrollArea>
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Public URL</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Views</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Unique</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Avg. Time</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Avg. Scroll</Table.Th>
                  <Table.Th>Last Visit</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pages.map((p) => (
                  <Table.Tr key={p.slug} style={{ cursor: 'pointer' }} onClick={() => navigate(`/hosting/${p.slug}`)}>
                    <Table.Td>
                      <Group gap={6} wrap="nowrap">
                        <Anchor component={Link} to={`/hosting/${p.slug}`} fw={600} onClick={(e) => e.stopPropagation()}>
                          {p.title || p.slug}
                        </Anchor>
                        {p.status === 'draft' && (
                          <Badge size="xs" variant="light" color="yellow">Draft</Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap={4} wrap="nowrap">
                        <Text size="xs" c="dimmed" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.url}
                        </Text>
                        <CopyButton value={p.url} timeout={1500}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? 'Copied' : 'Copy URL'} withArrow>
                              <ActionIcon variant="subtle" color={copied ? 'teal' : 'gray'} onClick={copy} size="sm">
                                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </CopyButton>
                        <Tooltip label="Open page" withArrow>
                          <ActionIcon variant="subtle" color="gray" component="a" href={p.url} target="_blank" rel="noopener noreferrer" size="sm">
                            <IconExternalLink size={14} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}><Text size="sm" fw={500}>{p.totalViews}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}><Text size="sm">{p.uniqueVisitors}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}><Text size="sm">{formatDuration(p.avgTimeOnPage)}</Text></Table.Td>
                    <Table.Td style={{ textAlign: 'center' }}><Text size="sm">{p.avgScroll || 0}%</Text></Table.Td>
                    <Table.Td><Text size="xs" c="dimmed">{formatDate(p.lastVisitAt)}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Stack>

      <Modal opened={opened} onClose={close} title="Add hosted page" size="md" centered>
        <form onSubmit={handleCreate}>
          <Stack gap="sm">
            <FileInput
              label="Upload HTML or ZIP file"
              description="Upload a single .html file or a .zip containing index.html and its assets."
              placeholder="Choose a .html or .zip file"
              accept=".html,.htm,.zip,application/zip"
              leftSection={<IconUpload size={16} />}
              value={file}
              clearable
              onChange={handleFile}
            />
            {title && (
              <Text size="sm" c="dimmed">
                Detected title: <Text span fw={600} c="dark">{title}</Text>
              </Text>
            )}
            <Group justify="flex-end" mt="sm">
              <Button variant="default" onClick={close}>Cancel</Button>
              <Button type="submit" loading={creating} disabled={!file}>Publish page</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Box>
  );
}
